import { BeneficiaryTransferDestination } from './beneficiaries'
import { WithPaginationApiResponseExtended } from './pagination'
import { PaymentContextType } from './payments'

export const ContactEntity = {
  COMPANY: 'COMPANY',
  INDIVIDUAL: 'INDIVIDUAL',
} as const

export type ContactEntityType = (typeof ContactEntity)[keyof typeof ContactEntity]

export const RelationshipType = {
  THIRD_PARTY: 'THIRD_PARTY',
  SELF_OWNED: 'SELF_OWNED',
} as const

export type RelationshipType = (typeof RelationshipType)[keyof typeof RelationshipType]

export const InstrumentType = {
  CRYPTO: 'CRYPTO',
  BANK_TRANSFER: 'BANK_TRANSFER',
  BANK_ACCOUNT: 'BANK_ACCOUNT',
} as const

export type InstrumentType = (typeof InstrumentType)[keyof typeof InstrumentType]
export interface ContactEntityAddress {
  addressLine1: string
  addressLine2?: string
  city: string
  region?: string
  postCode: string
  country: string
}

export interface ContactEntity {
  type: ContactEntityType
  address: ContactEntityAddress
}

export interface ContactEntityIndividual extends ContactEntity {
  firstName: string
  lastName: string
  dateOfBirth: string
}

export interface ContactEntityCompany extends ContactEntity {
  legalName: string
  registrationNumber: string
  dateOfIncorporation?: string
}

export interface ContactPayload {
  description: string
  relationshipType: RelationshipType
  entity: ContactEntityIndividual | ContactEntityCompany
}

export interface Contact extends ContactPayload {
  id: string
  name: string
  defaultReference: string
  updatedAt: Date
  createdAt: Date
}

export interface ContactCaseCount {
  count: number
}

export interface ContactCase {
  externalId: string
  hash: string
  currencyCode: string
  network: string
  amount: number
  paymentCreated: Date
  paymentContext: ContactCasePaymentContext
}

export interface ContactCasePaymentContext {
  paymentExternalId: string
  type: PaymentContextType
  channelUUID?: string
}

export interface Address {
  addressLine1: string
  addressLine2: string
  city: string
  region: string
  postCode: string
  country: string
}

export interface IntermediaryBank {
  name: string
  identificationCode?: string
  swiftCode?: string
  address?: Address
}

export interface ContactInstrumentAccountHolderName {
  firstName?: string
  lastName?: string
  legalName?: string
}

export interface ContactInstrument {
  // Common fields
  type: InstrumentType
  contact?: Contact
  provider?: string
  transferDestination?: string
  updatedAt?: string
  createdAt?: string
  verification?: {
    type: VerificationType
    status: VerificationStatus
  }
  id: string
  currency: string
  description: string
  // Crypto fields
  address?: string
  tag?: string
  network?: string
  selfHosted?: boolean
  // Bank account fields
  accountNumber?: string
  accountType?: string
  paymentMethods?: string[]
  bank?: ContactBankAccountBank
  accountHolderName?: ContactInstrumentAccountHolderName
  accountHolderAddress?: Address
  intermediaryBanks?: IntermediaryBank[]
}

export type ContactInstruments = WithPaginationApiResponseExtended<ContactInstrument[]>

export type Contacts = WithPaginationApiResponseExtended<Contact[]>

export const VerificationType = {
  NONE: 'NONE',
  SCREENSHOT: 'SREENSHOT',
} as const

export type VerificationType = (typeof VerificationType)[keyof typeof VerificationType]

export const VerificationStatus = {
  UNVERIFIED: 'UNVERIFIED',
  VERIFICATION_REQUESTED: 'VERIFICATION_REQUESTED',
  VERIFIED: 'VERIFIED',
} as const

export type VerificationStatus = (typeof VerificationStatus)[keyof typeof VerificationStatus]

export interface ContactsRequest {
  size?: number
  page?: number
  searchQuery?: string
  contactIds?: string[]
  contactId?: string
  type?: ContactEntityType
  totalCountLimit?: number
}

export interface ContactInstrumentPayload {
  type: InstrumentType
  address: string
  network: string
  currency: string
  description: string
}

export interface ContactInstrumentProps {
  instrumentId?: string
  contactId: string
  instrument?: ContactInstrumentPayload | CreateContactBankAccountRequest
}

export interface PutContactProps {
  contactId: string
  contact: ContactPayload
}

export type ContactPaymentInstrument = {
  id: string
  currency: string
  description: string
  updatedAt: string
  createdAt: string
  address: string
  tag?: string | null
  network: string
  type: InstrumentType
}

export type ContactPaymentInstruments = {
  paymentInstrument: CryptoPaymentInstrument | BankAccountPaymentInstrument
  contact: PaymentInstrumentContact
}

export interface ContactPaymentInstrumentsResponse
  extends WithPaginationApiResponseExtended<ContactPaymentInstruments[]> {}

export interface PostContactCase {
  contactId: string
  externalId: string
}

// Bank account instruments
interface ContactBankAccountBank {
  name: string
  identificationCode?: string
  swiftCode?: string
  address: Address
}

export interface AccountHolderName {
  firstName?: string
  lastName?: string
  legalName?: string
}
export interface CreateContactBankAccountRequest {
  currency: string
  description: string
  destinations?: BeneficiaryTransferDestination[]
  accountNumber: string
  accountHolderName: AccountHolderName
  accountHolderAddress: Address
  bank: ContactBankAccountBank
  paymentMethods: string[]
  intermediaryBanks?: IntermediaryBank[]
  type: InstrumentType
}

export interface CreateContactBankAccountResponse {}

export interface CreateContactBankAccountInstrumentProps {
  contactId: string
  request?: CreateContactBankAccountRequest
}

export const ContactInstrumentDecisionStep = {
  CONTACT: 'CONTACT',
  CONTACT_DECISION: 'CONTACT_DECISION',
  INSTRUMENT: 'INSTRUMENT',
} as const

export type ContactInstrumentDecisionStep =
  (typeof ContactInstrumentDecisionStep)[keyof typeof ContactInstrumentDecisionStep]

interface BasePaymentInstrument {
  id: string
  currency: string
  description: string
  updatedAt: string
  createdAt: string
  type: InstrumentType
}
interface CryptoPaymentInstrument extends BasePaymentInstrument {
  address: string
  tag?: string | null
  network: string
}

export interface BankAccountPaymentInstrument extends BasePaymentInstrument {
  accountNumber: string
  accountType?: string
  intermediaryBanks?: IntermediaryBank[]
  bank: ContactBankAccountBank
  accountHolderName: AccountHolderName
  accountHolderAddress: Address
  paymentMethods: string[]
}

interface PaymentInstrumentContact {
  id: string
  name: string
  description: string
  relationshipType: RelationshipType
  entity: ContactEntityIndividual | ContactEntityCompany
  updatedAt: string
  createdAt: string
}
